import { Link } from 'react-router-dom';
import EventCard from '../components/EventCard';
import Countdown, { FESTIVITIES_START } from '../components/Countdown';
import OfflinePill from '../components/OfflinePill';
import PhotoTile from '../components/PhotoTile';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import { playlistOf } from '../lib/content';
import { useMedia, visibleTo } from '../lib/media';
import { useSession } from '../lib/session';
import TeamStanding from '../components/TeamStanding';
import PlaylistCard from '../components/PlaylistCard';
import TripCard from '../components/TripCard';
import ExtraNote, { extrasToday } from '../components/ExtraNote';
import WeatherNow from '../components/WeatherNow';
import { useWeather } from '../lib/weather';
import { MarkTrophy } from '../components/Icon';
import { dayKey, fmtDay, untilLabel } from '../lib/time';

/**
 * Now — the landing screen and the most-used one.
 *
 * NOT on the canvas: the "try next" line asks for Now and the map to be drawn,
 * so rather than invent a new visual language this page is assembled from
 * components the artboards already settled — the hero event card from 1d, the
 * tile from 1h, and since this spike the two-column shell from 1l.
 *
 * **Three questions.** They used to be interleaved — the team card and the
 * playlist sat in the middle of the schedule — and they are now three slots:
 *
 *   1. what is happening — countdown, "Right now", later today;
 *   2. what is mine — my table, my room, my flights, my team, the playlist;
 *   3. what is everyone doing — the photographs coming in.
 *
 * **On a phone, (2) comes first, and that was a deliberate reversal.** The page
 * shipped with the schedule above the rail on the argument that what is true
 * *now* outranks what is true all week. On a 390px screen that argument loses to
 * arithmetic: the countdown and the hero card are 300px between them, which put
 * a guest's room and table below the fold and the photo strip off the screen
 * entirely. The two facts looked up most often — where am I sleeping, where am I
 * sitting — were the two that cost a scroll, and the hero card is only pushed
 * down by a card the same guest wanted anyway.
 *
 * So the DOM order is slot 2, 1, 3, and **that is the phone's scroll order** —
 * nothing here is reordered by CSS, which is what keeps the two widths from
 * silently disagreeing (`Now.test.tsx` asserts the source order for exactly
 * that reason). At 900px the grid places the rail in a second column and the
 * source order stops mattering: (1) and (3) stack in the main column, (2) is a
 * sticky rail beside them, the same shape the Schedule uses. Nothing is
 * conditional on width in JavaScript — one tree, and CSS decides.
 */
export default function Now() {
  const model = useScheduleModel();
  const session = useSession();
  const media = useMedia();
  const shared = visibleTo(media);
  /* Called before the loading return below, like every other hook here — the
     weather must not be conditional on the schedule having arrived, or the
     fetch would restart every time that resolved. */
  const weather = useWeather();

  if (model.loading) return <div className="page page-narrow"><SkeletonEvents rows={2} /></div>;

  const focus = model.current ?? model.next;
  /**
   * The playlist rides on the reception, whose window already runs to 3am — the
   * after party is the tail of that event rather than one of its own (D57).
   *
   * Shown from whenever the site is first opened until that window closes,
   * which is not as broad as it sounds: there is no pre-trip login (D8), so in
   * practice it appears when a guest arrives and collects songs across the
   * week. Gating it to "reception is next" would have surfaced it at seven in
   * the evening on the last night, having missed almost every chance to add
   * anything.
   *
   * Found by looking for a playlist rather than by naming `reception`, so
   * moving it to another event is a `schedule.json` edit and no deploy (D12).
   */
  const playlistEvent = model.events.find(
    (e) => playlistOf(e) && Date.parse(e.end ?? e.start) > model.at,
  );
  /**
   * The day's massage, if today is one of the three (D61). Not an event and
   * never in `events` — see `Extra` — so nothing else on the site shows it and
   * the schedule stays the programme.
   */
  const extras = extrasToday(model.extras, model.at);
  const todayRest = model.events.filter(
    (e) => dayKey(e.start) === dayKey(model.at) && e.id !== focus?.id && model.stateOf(e) !== 'past',
  );

  return (
    <div className="page now-page">
      <OfflinePill fetchedAt={model.fetchedAt} />

      {/* Removes itself on 5 November — see `Countdown`. Above the split, and
          full width: before the week it is the reason to open the page, and
          during the week it is gone, so it costs that space nothing. */}
      <Countdown />

      {/* Three slots, not two. The obvious shape is a main column and a rail,
          but `.cols` collapses to main-then-rail on a phone — which would put
          my table and my flights *below* the photo strip. Splitting the tail
          off lets one DOM order read correctly at both widths: on a phone the
          rail leads and the photographs follow the schedule; on a laptop the
          grid lifts the rail into its own column and the order is moot. */}
      <div className="now-cols">
        {/* Everything that is true of *this guest* and nobody else, in the
            order they are wanted: the dates before the trip, the table and the
            room during it, the standing once there is one, the playlist while
            it can still be added to. Empty for an unclaimed guest, and the
            wrapper goes with it — an empty sticky rail on a laptop is a column
            of whitespace beside the schedule. */}
        {(session?.guestId || session?.team || playlistEvent || extras.length > 0) && (
          <aside className="now-side">
            {/* Keyed on holding a roster row, **not** on the row having
                anything on it. `trip` is absent whenever all four fields are
                empty, which today is all 164 rows — and a guest who sees
                nothing cannot tell whether we are withholding their flights or
                have never been given them. The card says which, so the check
                here is only "is there a row this could be about at all".
                `?? {}` because absent and empty mean the same thing to it. */}
            {session?.guestId && <TripCard trip={session.trip ?? {}} />}
            {/* **The two-up shelf, and why the class is conditional.** Side by
                side these are 80px of a phone where stacked they are 140, and
                neither has enough to say to earn a full-width row of its own —
                a flag with a number, and a link that leaves the app.

                `now-pair-two` is set here rather than asked for in CSS with
                `:has()` because it is a fact this component already knows, and
                because what it guards is real: the compact tile drops the
                chevron, hides the playlist's description and folds the points
                onto a second line, all of which are answers to a 173px column.
                Applied to a card sitting alone at full width they would be
                damage. One of the two is missing more often than not — an
                unclaimed guest is on no team (D60), and the playlist goes when
                the reception window closes — so the single-card case is the
                common one, and it stays exactly as it was. */}
            {(session?.team || playlistEvent) && (
              <div className={session?.team && playlistEvent ? 'now-pair now-pair-two' : 'now-pair'}>
                {/* No team, no standing. Before a guest claims their name there
                    is nothing to be placed against (D60), and a card reading
                    "8th" for a team they are not on is worse than no card. */}
                {session?.team && <TeamStanding team={session.team} />}
                {/* Only while it can still be acted on. A playlist you can no
                    longer add to is a dead row. */}
                {playlistEvent && <PlaylistCard playlist={playlistOf(playlistEvent)!} />}
              </div>
            )}
            {/* Last in the rail, and the shortest thing in it. It is the only
                item here that is not about this particular guest, so it sits
                below the ones that are — and on a phone that puts it directly
                above "Up next", which is where somebody looking for what to do
                with their afternoon is already reading. */}
            {extras.map((extra) => (
              <ExtraNote key={extra.id} extra={extra} venue={model.venueOf(extra.venueId)} />
            ))}
          </aside>
        )}

        <div className="now-main">
          {focus ? (
            <>
              {/* The weather rides on this line rather than owning a row of
                  its own. It was a full-width card and cost 63px of a phone's
                  first screen for a temperature; as a pill it costs none, and
                  the detail is a tap away. It renders nothing until there is a
                  current reading, so this heading is otherwise unchanged. */}
              <div className="day-head">
                <h4>{model.current ? 'Right now' : 'Up next'}</h4>
                <WeatherNow weather={weather} />
                <span className="day-note">{fmtDay(focus.start)}</span>
              </div>
              <div className="ev-list" data-tour="events">
                <EventCard
                  event={focus}
                  venue={model.venueOf(focus.venueId)}
                  state={model.stateOf(focus)}
                  at={model.at}
                  density="hero"
                />
              </div>
              {focus.notes && (
                <div className="page-pad" style={{ paddingTop: 14 }}>
                  <h5 style={{ margin: '0 0 5px' }}>Notes from us</h5>
                  <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>{focus.notes}</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty">
              {/* **The weather survives the end of the programme.** Moving the
                  pill onto the "Right now" heading put it inside this
                  ternary's other branch, so once the last event has finished
                  it silently stopped rendering — and the last day of the week
                  is exactly when a guest is checking the sky before a flight
                  out. It was unconditional before the pill existed and it is
                  unconditional again; `wx-solo` only undoes the auto margin
                  that pushes it towards a date there is no longer one of. */}
              <div className="wx-solo"><WeatherNow weather={weather} /></div>
              <MarkTrophy className="mark" size={44} />
              <h4 style={{ margin: 0 }}>That's the week</h4>
              <p>
                Thank you for coming all this way. Everything anybody photographed is still here, and it is
                staying here — have a look through it when you land.
              </p>
              <Link className="btn btn-primary" to="/gallery">Open the gallery</Link>
            </div>
          )}

          {todayRest.length > 0 ? (
            <>
              <div className="day-head"><h4>Later today</h4></div>
              <div className="ev-list">
                {todayRest.map((event) => (
                  <EventCard
                    key={event.id} event={event} venue={model.venueOf(event.venueId)}
                    state={model.stateOf(event)} at={model.at} density="row"
                  />
                ))}
              </div>
            </>
          ) : focus && model.stateOf(focus) !== 'now' && model.at >= FESTIVITIES_START && (
            /* Not while the countdown is still on screen. Before the week
               starts this sentence says, in two lines, what the flip board says
               in four digits and the hero card repeats in its own badge — three
               statements of "not for 48 days" stacked down a phone. It earns
               its place from the 5th onwards, when "nothing else today" is
               genuinely news about today rather than a restatement of the
               wait. */
            <p className="page-pad" style={{ fontSize: 13, opacity: 0.6, marginTop: 14 }}>
              Nothing else today — the next thing is {fmtDay(focus.start)}, {untilLabel(focus.start, model.at)}.
            </p>
          )}
        </div>

        <div className="now-tail">
          {shared.length > 0 && (
            <>
              <div className="day-head">
                <h4>Coming in</h4>
                <Link to="/gallery" style={{ fontSize: 12, textDecoration: 'none' }}>{shared.length} photos so far</Link>
              </div>
              <div className="strip">
                {shared.slice(0, 12).map((item) => <PhotoTile key={item.id} item={item} />)}
              </div>
            </>
          )}
          {/* The weather credit used to ride here. It is a licence condition
              (CC BY 4.0) and now sits in the site footer, where it is on every
              screen rather than appearing and vanishing with the forecast. */}
          <div className="tz-note">All times Khao Lak, GMT+7</div>
        </div>
      </div>
    </div>
  );
}
